import SafeUncontrolledPopover from 'components/SafeUncontrolledPopover';
import { uniqueId as lodashUniqueId } from 'lodash';
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ConnectDragSource, ConnectDropTarget } from 'react-dnd';
import { FormattedMessage, useIntl } from 'react-intl';
import { Col, Input, Label, Row } from 'reactstrap';
import {
  DRAG_AND_DROP_ICON,
  DraggableItemTypes,
  useDragAndDrop,
} from 'utils/util/util';
import Checkbox from 'views/Widgets/Inputs/Checkbox';
import { ColumnDescriptor } from './types';

interface RowProps {
  field: string;
  label: ReactNode;
  index: number;
  checked: boolean;
  disabled: boolean;
  onToggle: (field: string) => void;
  moveColumn: (fromIndex: number, toIndex: number) => void;
}

const ColumnRow: FC<RowProps> = ({
  field,
  label,
  index,
  checked,
  disabled,
  onToggle,
  moveColumn,
}) => {
  const { formatMessage } = useIntl();
  const dragId = useRef(lodashUniqueId('sortable-column-'));
  // the shared hook is untyped and returns a heterogeneous tuple
  const [dragAndDropRef, handlerId, drop, isDragging, drag] = useDragAndDrop(
    DraggableItemTypes.TABLE_COLUMN,
    dragId.current,
    index,
    moveColumn,
    !disabled
  ) as unknown as [
    React.RefObject<HTMLDivElement>,
    string | symbol | null,
    ConnectDropTarget,
    boolean,
    ConnectDragSource,
  ];

  useEffect(() => {
    drag(drop(dragAndDropRef));
  }, [drag, drop, dragAndDropRef]);

  const checkboxId = `sortable-column-check-${field}`;

  return (
    <div
      ref={dragAndDropRef}
      data-handler-id={handlerId}
      data-testid={`sortable-column-${field}`}
      className="d-flex align-items-center py-1"
      style={{ opacity: isDragging ? 0.4 : 1 }}
    >
      <span
        role="button"
        className="text-muted me-2"
        style={{ cursor: disabled ? 'default' : 'move', flexShrink: 0 }}
        aria-label={formatMessage({
          id: 'app.views.widgets.people.filterable_people_table_column_selector_modal.sortable_column_selector.drag_handle',
          defaultMessage: 'Drag to reorder',
        })}
      >
        {!disabled && DRAG_AND_DROP_ICON}
      </span>
      <Input
        type="checkbox"
        id={checkboxId}
        name={field}
        className="mt-0 me-2"
        checked={checked}
        disabled={disabled}
        onChange={() => onToggle(field)}
      />
      <Label for={checkboxId} className="mb-0" role="button">
        <span id={field}>{label}</span>
      </Label>
    </div>
  );
};

interface Props {
  value?: string[];
  onChange?: (fields: string[]) => void;
  columnDescriptors?: ColumnDescriptor[];
  disabled?: boolean;
}

/**
 * Column picker for any table: which columns show, and in what order.
 *
 * Selection and order are one control because they are one decision. The
 * value is the selected fields in display order, so whatever consumes it
 * gets both answers from one list. Ticking a box never moves a row, so an
 * order survives a column being toggled off and back on.
 */
const SortableColumnSelector: FC<Props> = ({
  value,
  onChange,
  columnDescriptors = [],
  disabled = false,
}) => {
  const selected = useMemo(() => value ?? [], [value]);

  // Display order for every column, selected or not - the saved ones first, in
  // their saved order, then the rest as the descriptors list them.
  const [order, setOrder] = useState<string[]>(() => {
    const all = columnDescriptors.map((column) => column.field);
    return [...selected.filter((f) => all.includes(f))].concat(
      all.filter((f) => !selected.includes(f))
    );
  });

  // Columns can appear or disappear while the modal is open (a customer-data
  // column toggled available). New ones join at the end rather than disturbing
  // an order already set.
  useEffect(() => {
    setOrder((current) => {
      const all = columnDescriptors.map((column) => column.field);
      const kept = current.filter((f) => all.includes(f));
      const added = all.filter((f) => !kept.includes(f));
      return kept.length === current.length && !added.length
        ? current
        : [...kept, ...added];
    });
  }, [columnDescriptors]);

  const emit = useCallback(
    (nextOrder: string[], nextSelected: string[]) => {
      onChange?.(nextOrder.filter((field) => nextSelected.includes(field)));
    },
    [onChange]
  );

  const moveColumn = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setOrder((current) => {
        const next = [...current];
        const [moved] = next.splice(dragIndex, 1);
        next.splice(hoverIndex, 0, moved);
        emit(next, selected);
        return next;
      });
    },
    [emit, selected]
  );

  const onToggle = useCallback(
    (field: string) => {
      emit(
        order,
        selected.includes(field)
          ? selected.filter((f) => f !== field)
          : [...selected, field]
      );
    },
    [emit, order, selected]
  );

  const allSelected = order.length > 0 && selected.length === order.length;

  // Tri-state, as the checkbox selector's select_all was: ticked for all, a
  // dash for some, empty for none. Checkbox reads undefined as the dash.
  const selectAllValue = allSelected
    ? true
    : selected.length
      ? undefined
      : false;

  const onToggleAll = useCallback(() => {
    emit(order, allSelected ? [] : order);
  }, [emit, order, allSelected]);

  const descriptorsByField = useMemo(
    () => new Map(columnDescriptors.map((column) => [column.field, column])),
    [columnDescriptors]
  );

  return (
    <div>
      {/* Above the checkbox, where ValidatedInput puts an input's helperText. */}
      <small className="form-text text-muted">
        <FormattedMessage
          id="app.views.widgets.people.filterable_people_table_column_selector_modal.sortable_column_selector.helper_text"
          defaultMessage="Select the fields to display, and drag them into the order you want them shown."
        />
      </small>
      <div className="d-flex align-items-center pb-2">
        <Checkbox
          name="select_all"
          className="mt-0 me-2"
          value={selectAllValue}
          withIndeterminate
          disabled={disabled}
          onChange={onToggleAll}
          label={
            <span className="fw-bold">
              <FormattedMessage
                id="app.views.widgets.people.filterable_people_table_column_selector_modal.sortable_column_selector.select_all"
                defaultMessage="Select all"
              />
            </span>
          }
        />
      </div>
      {/* Same 1/2/3-across grid the checkbox selector used. Note the shared
          drag helper compares vertical position only, so dragging across
          columns is imprecise - dragging within a column is exact. */}
      <Row xs={1} sm={2} md={3}>
        {order.map((field, index) => {
          const descriptor = descriptorsByField.get(field);
          return (
            <Col key={field}>
              <ColumnRow
                field={field}
                label={descriptor?.fullName ?? descriptor?.name ?? field}
                index={index}
                checked={selected.includes(field)}
                disabled={disabled}
                onToggle={onToggle}
                moveColumn={moveColumn}
              />
              {!!descriptor?.popoverContent && (
                <SafeUncontrolledPopover
                  placement="top"
                  trigger="hover"
                  target={field}
                >
                  {descriptor.popoverContent}
                </SafeUncontrolledPopover>
              )}
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default SortableColumnSelector;
